/**
 * Find Your Vehicle finder (RAZ-327).
 *
 * The host behind blocks/rb-vehicle-finder.liquid. It owns exactly three decisions
 * the shared cascade deliberately refuses to make:
 *
 *   1. when the CTA is live,
 *   2. what the CTA says — while it is not, and per the submit mode it is set to,
 *   3. what a submit does: set the vehicle in place (the default) or go where the
 *      resolver says, and what happens when the answer is nowhere.
 *
 * Everything else — options, auto-advance, the saved-vehicle seed, the live region —
 * comes from the shared `vehicleSelector` component, which this one composes rather
 * than copies. Nothing here re-derives cascade or fitment logic: the destination is
 * always the resolver's own resolveDestination()/destinationUrl() answer, so the
 * finder, the header panel and the PDP card can never disagree about a URL.
 *
 * Composition is done with defineProperties rather than a spread, because a spread
 * would read every getter on the cascade once and freeze the answers.
 */

import { IMPOSSIBLE, destinationUrl, fill } from '../utils/vehicleResolver.js';
import vehicleSelector from './vehicleSelector.js';

/** Resolver level to the id suffix snippets/rb-vehicle-selector-fields.liquid writes. */
const FIELD_ID_SUFFIX = {
  make: 'make',
  model: 'model',
  year: 'year',
  trim: 'trim',
  band: 'band',
};

/**
 * What a submit does once the vehicle is saved.
 *
 * `stay` is the default everywhere: naming a vehicle is the shopper telling the site
 * what they drive, not asking to be moved. `redirect` is the journey the Figma hero
 * annotation drew ("Takes User directly to relevant PLP"), kept as a per-placement
 * choice so a merchant can still have it.
 */
const AFTER_SUBMIT_STAY = 'stay';
const AFTER_SUBMIT_REDIRECT = 'redirect';

/**
 * Copy used until the block hands its own settings over.
 *
 * The CTA has one default per mode, because the label is a promise about what pressing
 * it does: "Find my covers" with its arrow promises a trip to the covers, and on a
 * finder that stays put that promise is a lie. The merchant can still override either.
 */
const DEFAULT_COPY = {
  ctaLabelRedirect: 'Find my covers',
  ctaLabelStay: 'Save my vehicle',
  chooseNextText: 'Choose your {field} to continue',
  noMatchLinkText: 'Shop all {model}',
  savedText: 'Vehicle saved \u00b7 {make} {model}',
};

/** The hub the no-match exit falls back to when the block names none. */
const DEFAULT_HUB_URL = '/pages/vehicle-hub';

/**
 * The classes the pre-paint script in blocks/rb-vehicle-finder.liquid puts on <html>.
 *
 * `--year` and `--trim` say the mobile card should hold room for the Year and Variant
 * fields because the saved vehicle is going to fill them. `--unverified` says that was a
 * guess: the shopper's cached catalogue was cold, so nothing could be checked before
 * paint. `--hold` is this component's answer when the guess turns out to be wrong.
 */
const RESERVED_YEAR = 'rb-vehicle-saved--year';
const RESERVED_TRIM = 'rb-vehicle-saved--trim';
const RESERVED_UNVERIFIED = 'rb-vehicle-saved--unverified';
const RESERVED_HOLD = 'rb-vehicle-saved--hold';

/** How long the save-and-stay confirmation stays on screen — the panel's four seconds. */
const CONFIRM_DURATION = 4000;

/**
 * The one debug warning this component emits, guarded so production consoles stay quiet.
 * @param {string} message
 * @param {*} detail
 */
function warnOnce(message, detail) {
  if (typeof window !== 'undefined' && window.enableDebugMode) { console.warn(message, detail); }
}

/**
 * A non-blank string from the config, or the fallback.
 * @param {*} value
 * @param {string} fallback
 * @returns {string}
 */
function copyOr(value, fallback) {
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

export default {
  name: 'vehicleFinder',

  /**
   * @param {Object} [params] every vehicleSelector param, plus the ones below
   * @param {string} [params.idPrefix] the snippet's id prefix, used to move focus
   * @param {string} [params.hubUrl] the vehicle hub, the last-resort exit
   * @param {string} [params.ctaLabel] CTA copy once a destination resolves; blank takes
   *   the default for the submit mode below
   * @param {'stay'|'redirect'} [params.afterSubmit] what a submit does once the vehicle
   *   is saved (default 'stay')
   * @param {string} [params.savedText] the save-and-stay confirmation, with {model}
   * @param {string} [params.chooseNextText] CTA copy while a field is unanswered
   * @param {string} [params.noMatchLinkText] the no-match panel's "shop all {model}" exit
   * @param {Function} [params.navigate] injected by the unit tests in place of location
   * @returns {Object}
   */
  component(params = {}) {
    const config = params && typeof params === 'object' ? params : {};
    const cascade = vehicleSelector.component(config);

    // Held before the merge, so the override below can still call the original.
    const cascadeInit = cascade.init;

    const afterSubmit = config.afterSubmit === AFTER_SUBMIT_REDIRECT
      ? AFTER_SUBMIT_REDIRECT
      : AFTER_SUBMIT_STAY;
    const defaultCtaLabel = afterSubmit === AFTER_SUBMIT_REDIRECT
      ? DEFAULT_COPY.ctaLabelRedirect
      : DEFAULT_COPY.ctaLabelStay;

    const finder = {
      /* ---------------------------------------------------------------- */
      /* State                                                            */
      /* ---------------------------------------------------------------- */

      /** Prefix of every select id the snippet wrote, so focus can be moved. */
      idPrefix: copyOr(config.idPrefix, ''),

      /** Where "Browse all vehicles" and the no-JavaScript form both point. */
      hubUrl: copyOr(config.hubUrl, DEFAULT_HUB_URL),

      /** Merchant copy from the block's settings. */
      copy: {
        ctaLabel: copyOr(config.ctaLabel, defaultCtaLabel),
        chooseNextText: copyOr(config.chooseNextText, DEFAULT_COPY.chooseNextText),
        noMatchLinkText: copyOr(config.noMatchLinkText, DEFAULT_COPY.noMatchLinkText),
        savedText: copyOr(config.savedText, DEFAULT_COPY.savedText),
      },

      /** What a submit does once the vehicle is saved: 'stay' or 'redirect'. */
      afterSubmit,

      /** True while the save-and-stay confirmation is on screen. */
      isConfirming: false,

      /**
       * What the confirmation says. Written once per save and never cleared, so the
       * card's live region announces it exactly once — clearing it four seconds later
       * would read the same save out a second time.
       */
      confirmMessage: '',

      /** Timer that hides the confirmation, kept so destroy() can cancel it. */
      confirmTimer: null,

      /* ---------------------------------------------------------------- */
      /* Lifecycle                                                        */
      /* ---------------------------------------------------------------- */

      async init() {
        await cascadeInit.call(this);
        this.settleReservation();
      },

      /**
       * Settle the pre-paint room reservation against what the cascade actually did.
       *
       * Only an unverified reservation — one made against a cold catalogue cache — can be
       * wrong, and it is wrong in exactly one situation: the shopper's saved vehicle is no
       * longer in the catalogue, so the store has dropped it and the cascade has emptied
       * itself. Collapsing the card from four fields to two at that moment is a 0.16
       * layout shift at 390px, for a change the shopper neither asked for nor caused.
       *
       * So the room is held for the rest of this page load. The two fields stay in the
       * waiting state they are already painted in, which is the layout every desktop
       * shopper sees; the card is honest — two live fields and no ghost data — and nothing
       * moves. The store has cleared `rb_vehicle`, so the next page load reserves nothing
       * and paints the correct two-field card from its first frame.
       *
       * Runs synchronously after the cascade has seeded, before Alpine flushes aria-busy,
       * so the hold is in place by the time the busy-state rule stops applying.
       */
      settleReservation() {
        try {
          const root = document.documentElement;
          if (!root.classList.contains(RESERVED_UNVERIFIED)) return;
          root.classList.remove(RESERVED_UNVERIFIED);

          // A reservation the cascade honoured needs nothing: those fields are answerable,
          // so they are no longer pending and the reveal rule has stopped hiding them.
          const isKept =
            (!root.classList.contains(RESERVED_YEAR) || !this.isLevelDisabled('year'))
            && (!root.classList.contains(RESERVED_TRIM) || !this.isLevelDisabled('trim'));
          if (isKept) return;

          root.classList.add(RESERVED_HOLD);
        } catch (error) {
          warnOnce('vehicleFinder: the reserved card height could not be settled.', error);
        }
      },

      destroy() {
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        this.confirmTimer = null;
      },

      /* ---------------------------------------------------------------- */
      /* Is the CTA live?                                                 */
      /* ---------------------------------------------------------------- */

      /**
       * The CTA goes live as soon as every remaining candidate shares one destination,
       * which is well before the cascade runs out of questions: Ford Territory resolves
       * straight after Model, and a BYD Sealion variant resolves before its generation
       * is named. It never goes live on a guess.
       *
       * @returns {boolean}
       */
      get canSubmit() {
        if (!this.isReady || this.hasError) return false;
        if (!this.fields.model || this.isImpossible) return false;
        return this.destinationHref !== null;
      },

      /**
       * The selection has nowhere to send the shopper and never will: it matches no
       * fitment, or it has run out of questions on a fitment with no collection.
       * A selection that simply has more to answer is not unresolvable.
       *
       * @returns {boolean}
       */
      get isUnresolvable() {
        if (!this.isReady || this.hasError || !this.fields.model) return false;
        if (this.isImpossible) return true;
        return this.isComplete && this.destinationHref === null;
      },

      /* ---------------------------------------------------------------- */
      /* What the card says                                               */
      /* ---------------------------------------------------------------- */

      /**
       * The CTA's own label: the merchant's copy when it will navigate, and the field
       * that is still missing when it will not. Figma draws only the resting state,
       * so the "still needed" wording is this theme's own.
       *
       * @returns {string}
       */
      get ctaLabel() {
        if (this.canSubmit || !this.hasSelection) return this.copy.ctaLabel;
        const level = this.nextLevel;
        if (!level || level === IMPOSSIBLE) return this.copy.ctaLabel;
        return this.copy.chooseNextText
          .replace('{next_step}', this.labelForLevel(level))
          .replace('{field}', this.labelForLevel(level));
      },

      /**
       * "Shop all HiLux" — the way out of the no-match panel, and nowhere else.
       *
       * There used to be a second link carrying this same copy under the fields, shown
       * from the moment a model was chosen. It was never in the design, it appeared and
       * disappeared mid-cascade — growing the card, and with it the hero — and the
       * client-side lead asked for it gone. The panel below keeps its exit, because that
       * one is shown precisely when the CTA cannot take the shopper anywhere.
       *
       * @returns {string}
       */
      get noMatchLinkLabel() {
        return this.copy.noMatchLinkText.replace('{model}', this.modelLabel);
      },

      /**
       * The chosen model's own "shop all" collection, or null. Built by the resolver's
       * destinationUrl(), the one place a collection handle becomes a URL.
       */
      get modelCollectionHref() {
        return destinationUrl(this.modelCollection ? { collection: this.modelCollection } : null);
      },

      /** The nearest sensible destination when the selection resolves to nothing. */
      get fallbackHref() {
        return this.modelCollectionHref ?? this.hubUrl;
      },

      /* ---------------------------------------------------------------- */
      /* Submitting                                                       */
      /* ---------------------------------------------------------------- */

      /* ---------------------------------------------------------------- */
      /* Where a submit goes                                              */
      /* ---------------------------------------------------------------- */

      /**
       * Does a submit leave the page? Only where the placement asked it to.
       * @returns {boolean}
       */
      get shouldNavigateOnSubmit() {
        return this.afterSubmit === AFTER_SUBMIT_REDIRECT;
      },

      /**
       * Save the vehicle, then either go where the resolver says or say so and stay.
       *
       * The form is a real `<form method="get">` pointed at the hub, so a page with no
       * JavaScript — or with a catalogue that would not load — still submits. This only
       * takes the event over once the catalogue is in hand.
       *
       * @param {Event} [event] the submit event
       */
      submit(event) {
        // No catalogue: let the browser perform the plain GET to the hub instead.
        if (!this.isReady) return;
        if (event && typeof event.preventDefault === 'function') event.preventDefault();

        if (!this.canSubmit) {
          this.focusNextField();
          return;
        }

        const saved = this.saveVehicle();

        if (this.shouldNavigateOnSubmit) {
          this.navigate(this.destinationHref);
          return;
        }

        // Staying put with no word at all is indistinguishable from a form that broke,
        // so the card says what it did. The chip, the PDP card, the PLP bar and the cart
        // note need no help: the store announces the save and they re-render themselves.
        this.confirmSave(saved ?? this.vehicle);
        this.announceSubmit(saved ?? this.vehicle);
      },

      /**
       * Tell the page this finder was pressed. The store's own event cannot say so: it
       * fires for the header and other tabs too. The fitting guide block (RAZ-328) follows
       * a redirect on it, and only for the finder in its own section, so it bubbles from
       * this element for the listener to see where it came from.
       *
       * @param {Object|null} vehicle the vehicle as the store kept it
       */
      announceSubmit(vehicle) {
        try {
          this.$el?.dispatchEvent(new CustomEvent('vehicleFinder:submit', {
            bubbles: true,
            detail: { vehicle },
          }));
        } catch (error) {
          warnOnce('vehicleFinder: the submit could not be announced.', error);
        }
      },

      /**
       * Persist the selection so the header chip, the PLP bar and the PDP card can read
       * it — on this page, through the store's own `vehicleSet:success`, and on every
       * page after. A store that will not write is not worth losing the submit over.
       *
       * @returns {Object|null} the vehicle as the store kept it
       */
      saveVehicle() {
        try {
          return this.$store?.vehicle?.set(this.vehicle) ?? null;
        } catch (error) {
          warnOnce('vehicleFinder: the vehicle could not be saved.', error);
          return null;
        }
      },

      /**
       * Show the save-and-stay confirmation for four seconds, in the same words and for
       * the same reason as the header panel's.
       *
       * @param {Object|null} saved the vehicle the store kept
       */
      confirmSave(saved) {
        this.confirmMessage = fill(this.copy.savedText, saved) || this.copy.savedText;
        this.isConfirming = true;
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        this.confirmTimer = setTimeout(() => { this.isConfirming = false; }, CONFIRM_DURATION);
      },

      /**
       * Go to a destination. Injectable so the unit tests can read the URL without a
       * browser, and so a host can hand the finder its own router later.
       *
       * @param {string} href
       */
      navigate(href) {
        if (!href) return;
        if (typeof config.navigate === 'function') {
          config.navigate(href);
          return;
        }
        try {
          window.location.assign(href);
        } catch (error) {
          warnOnce('vehicleFinder: the destination could not be opened.', error);
        }
      },

      /**
       * Put the caret on the field the shopper still has to answer, so pressing a dead
       * CTA with the keyboard moves them forward rather than doing nothing at all.
       */
      focusNextField() {
        const level = this.nextLevel;
        if (!level || level === IMPOSSIBLE || !this.idPrefix) return;
        const suffix = FIELD_ID_SUFFIX[level];
        if (!suffix) return;
        try {
          const field = document.getElementById(`${this.idPrefix}-${suffix}`);
          if (field && typeof field.focus === 'function') field.focus();
        } catch (error) {
          warnOnce('vehicleFinder: the next field could not be focused.', error);
        }
      },
    };

    return Object.defineProperties(cascade, Object.getOwnPropertyDescriptors(finder));
  },
};
